import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const POLL_MS = 4000;

const getAuthToken = () =>
  localStorage.getItem("authToken")    ||
  localStorage.getItem("accessToken")  ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("token") || "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

// Instant-order status codes (see labour_service/views.py, InstantTab.js).
// 780+ range deliberately avoids colliding with the 0-9 scheduled-order
// ORDER_STATUS enum, which get_partner_bookings() aggregates alongside these.
const STATUS_SEARCHING  = 777;
const STATUS_ON_THE_WAY = 780;
const STATUS_ARRIVED    = 781;
const STATUS_COMPLETE   = 782;
const STATUS_CLOSED     = 783;
const STATUS_NO_PARTNER = 784; // company + public waterfall both exhausted
const STATUS_CANCELLED  = 785; // cancelled by customer — see handleCancel

// Default instant-booking cancellation tiers — overridden by the live config
// fetched from GET /api/order/config/instant/ (same doc the dashboard's
// Master Management "Cancellation Charges" panel edits).
const DEFAULT_INSTANT_CHARGES = [
  { condition: "before_accept", label: "Before partner accepts",                max_status: 777, percent: 0 },
  { condition: "after_accept",  label: "After partner accepts, before arrival", min_status: 780, max_status: 780, percent: 60 },
  { condition: "after_start",   label: "After partner arrived or started work", min_status: 781, percent: 95 },
];

// 2-minute free-cancellation window right after a partner accepts — mirrors
// INSTANT_ACCEPT_GRACE_SECS in views.py's _calc_cancel_info(). Keeps this
// preview honest instead of scaring the user with "60%" during the window
// the backend would actually charge ₹0 for.
const INSTANT_ACCEPT_GRACE_SECS = 120;

const secsSinceAccepted = (order) => {
  if (!order?.accepted_at) return null;
  const acceptedMs = Date.parse(order.accepted_at.replace(" ", "T") + "Z");
  if (Number.isNaN(acceptedMs)) return null;
  return (Date.now() - acceptedMs) / 1000;
};

// Client-side preview only — the real charge is computed authoritatively by
// the backend in the cancel POST. Mirrors _calc_cancel_info()'s tier match.
const previewCancelTier = (status, charges, order) => {
  if (status === STATUS_ON_THE_WAY) {
    const elapsed = secsSinceAccepted(order);
    if (elapsed != null && elapsed < INSTANT_ACCEPT_GRACE_SECS) {
      return { label: "Free cancellation (within 2 min of partner accepting)", percent: 0 };
    }
  }
  const tiers = [...charges].sort((a, b) => (b.min_status || 0) - (a.min_status || 0));
  for (const tier of tiers) {
    const minS = tier.min_status, maxS = tier.max_status;
    if (minS != null && maxS == null && status >= minS) return tier;
    if (minS != null && maxS != null && status >= minS && status <= maxS) return tier;
    if (minS == null && maxS != null && status <= maxS) return tier;
  }
  return charges[charges.length - 1];
};

const STEPS = ["Waiting", "On the way", "Arrived", "Work Done", "Confirmed"];

const stepIndexForStatus = (status) => {
  if (status === STATUS_CLOSED)   return 4;
  if (status === STATUS_COMPLETE) return 3;
  if (status === STATUS_ARRIVED)  return 2;
  if (status === STATUS_ON_THE_WAY) return 1;
  return 0; // searching / no partner found
};

// ─────────────────────────────────────────────────────────────────────────────
// INSTANT ORDER TRACKING
// Reached after Instant checkout (SummaryPage.js places the order unpaid,
// then redirects here). Polls the order's status and walks the customer
// through: searching → on the way → arrived (pay now) → work done (confirm)
// → closed. Payment reuses the existing generic PaymentPage.js via
// orderPayload.__submit_endpoint / __redirect_after_pay overrides.
// ─────────────────────────────────────────────────────────────────────────────
const InstantOrderTracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order,   setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [paying,     setPaying]     = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [instantCharges, setInstantCharges] = useState(DEFAULT_INSTANT_CHARGES);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/order/config/instant/`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (Array.isArray(data?.charges) && data.charges.length) setInstantCharges(data.charges);
      })
      .catch(() => {});
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!orderId) return;
    try {
      const res  = await fetch(`${API_BASE_URL}/api/user/instant-order/${orderId}/status/`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok && data?.success) {
        setOrder(data.data);
        setError("");
      } else if (res.status === 404) {
        setError("Order not found.");
      }
    } catch (e) {
      // Transient network hiccup — keep last known state, retry next tick.
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(intervalRef.current);
  }, [fetchStatus]);

  // Stop polling once the job is confirmed/closed, or the waterfall gave up
  // — nothing left to change either way.
  useEffect(() => {
    if (
      (order?.status === STATUS_CLOSED || order?.status === STATUS_NO_PARTNER || order?.status === STATUS_CANCELLED) &&
      intervalRef.current
    ) {
      clearInterval(intervalRef.current);
    }
  }, [order?.status]);

  const handlePayNow = async () => {
    if (paying || !order) return;
    setPaying(true);
    setError("");
    try {
      const amount = order.total_amount || order.amount || 0;
      const res  = await fetch(`${API_BASE_URL}/api/payment/create-order/`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ amount, mobile: order.mobile }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.message || "Could not initiate payment.");
      navigate("/payment", {
        state: {
          paymentOrder: data,
          orderPayload: {
            __submit_endpoint:     `/api/user/instant-order/${orderId}/pay/`,
            __redirect_after_pay:  `/instant-tracking/${orderId}`,
            mobile: order.mobile,
          },
          breakdown: { subtotal: amount, discount: 0, gst: 0, platformFee: 0, tip: 0, total: amount },
        },
      });
    } catch (e) {
      setError(e.message || "Payment initiation failed. Please try again.");
      setPaying(false);
    }
  };

  const handleConfirm = async () => {
    if (confirming) return;
    setConfirming(true);
    setError("");
    try {
      const res  = await fetch(`${API_BASE_URL}/api/user/instant-order/${orderId}/confirm/`, {
        method: "POST", headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.message || "Could not confirm. Please try again.");
      await fetchStatus();
    } catch (e) {
      setError(e.message || "Could not confirm. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = async () => {
    if (cancelling || !orderId) return;
    setCancelling(true);
    setError("");
    try {
      const res  = await fetch(`${API_BASE_URL}/api/orders/instant/${orderId}/cancel/`, {
        method: "POST", headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Could not cancel this booking. Please try again.");
      setShowCancelConfirm(false);
      await fetchStatus();
    } catch (e) {
      setError(e.message || "Could not cancel this booking. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div style={S.page}><div style={S.container}>
        <div style={S.centerNote}>Loading your booking…</div>
      </div></div>
    );
  }

  if (!order) {
    return (
      <div style={S.page}><div style={S.container}>
        <div style={S.centerNote}>{error || "Order not found."}</div>
        <Link to="/" style={S.homeLink}>← Back to Home</Link>
      </div></div>
    );
  }

  const status  = order.status;
  const stepIdx = stepIndexForStatus(status);
  const addr    = order.delivery_address || {};
  const address = [addr.line1, addr.area, addr.city].filter(Boolean).join(", ") || order.address || "";

  const amount = order.total_amount || order.amount || 0;
  const cancellable = [STATUS_SEARCHING, STATUS_ON_THE_WAY, STATUS_ARRIVED, STATUS_COMPLETE].includes(status);
  const previewTier   = previewCancelTier(status, instantCharges, order);
  const previewCharge = Math.round(amount * (previewTier.percent / 100));

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.card}>
          <div style={S.headRow}>
            <div>
              <p style={S.eyebrow}>Instant Booking</p>
              <h1 style={S.title}>{order.service_name || order.service || "Service"}</h1>
              <p style={S.orderIdText}>Order ID: {order.order_id}</p>
            </div>
            <div style={S.amountBadge}>{fmt(order.total_amount || order.amount)}</div>
          </div>

          {/* Step progress */}
          <div style={S.stepRow}>
            {STEPS.map((label, i) => (
              <React.Fragment key={label}>
                <div style={S.stepItem}>
                  <div style={{ ...S.stepDot, ...(i <= stepIdx ? S.stepDotActive : {}) }}>
                    {i < stepIdx ? "✓" : i + 1}
                  </div>
                  <div style={{ ...S.stepLabel, ...(i <= stepIdx ? S.stepLabelActive : {}) }}>{label}</div>
                </div>
                {i < STEPS.length - 1 && <div style={{ ...S.stepConnector, ...(i < stepIdx ? S.stepConnectorActive : {}) }} />}
              </React.Fragment>
            ))}
          </div>

          {status === STATUS_SEARCHING && (
            <div style={S.statusBox}>
              <div style={S.spinnerIcon}>⏳</div>
              <h2 style={S.statusTitle}>Waiting for a partner to accept your booking…</h2>
              <p style={S.statusText}>This usually takes a couple of minutes. We'll update this page automatically the moment a partner accepts.</p>
            </div>
          )}

          {status === STATUS_NO_PARTNER && (
            <div style={S.statusBox}>
              <div style={S.spinnerIcon}>😔</div>
              <h2 style={S.statusTitle}>No professional is available right now</h2>
              <p style={S.statusText}>Nobody could accept this instant request in your area at the moment.</p>

              <div style={S.recapBox}>
                <p style={S.recapLabel}>Booking details</p>
                <p style={S.recapLine}><strong>{order.service_name || order.service || "Service"}</strong></p>
                {address && <p style={S.recapLine}>{address}</p>}
                <p style={S.recapLine}>{fmt(order.total_amount || order.amount)}</p>
              </div>

              <p style={S.statusText}>You can schedule the same service for a confirmed time slot instead — no waiting, guaranteed booking.</p>
              <Link
                to={`/instant-tracking/${orderId}/reschedule`}
                style={S.primaryBtnLink}
              >
                Schedule This Service Instead
              </Link>
            </div>
          )}

          {status === STATUS_ON_THE_WAY && (
            <div style={S.statusBox}>
              <div style={S.spinnerIcon}>🧭</div>
              <h2 style={S.statusTitle}>{order.partner_name || "Your professional"} is on the way</h2>
              {order.partner_mobile && <p style={S.statusText}>📞 {order.partner_mobile}</p>}
              {address && <p style={S.statusText}>{address}</p>}
            </div>
          )}

          {status === STATUS_ARRIVED && (
            <div style={S.statusBox}>
              <div style={S.spinnerIcon}>📍</div>
              <h2 style={S.statusTitle}>{order.partner_name || "Your professional"} has arrived</h2>
              <div style={S.otpBox}>
                <p style={S.otpLabel}>Share this code with your professional to confirm arrival</p>
                <div style={S.otpCode}>{String(order.mobile || order.user_mobile || "").slice(-4)}</div>
              </div>
              {order.payment_status === "paid" ? (
                <p style={S.statusText}>✅ Payment received — work will begin shortly.</p>
              ) : (
                <>
                  <p style={S.statusText}>Please complete payment so the work can begin.</p>
                  <button style={S.primaryBtn} disabled={paying} onClick={handlePayNow}>
                    {paying ? "Starting payment…" : `Pay ${fmt(order.total_amount || order.amount)} Now`}
                  </button>
                </>
              )}
            </div>
          )}

          {status === STATUS_COMPLETE && (
            <div style={S.statusBox}>
              <div style={S.spinnerIcon}>✅</div>
              <h2 style={S.statusTitle}>Work completed</h2>
              <p style={S.statusText}>Please confirm the job is done to close this booking.</p>
              <button style={S.primaryBtn} disabled={confirming} onClick={handleConfirm}>
                {confirming ? "Confirming…" : "Confirm — Job Done"}
              </button>
            </div>
          )}

          {status === STATUS_CLOSED && (
            <div style={S.statusBox}>
              <div style={S.spinnerIcon}>🎉</div>
              <h2 style={S.statusTitle}>Booking confirmed and closed</h2>
              <p style={S.statusText}>Thanks for booking with us!</p>
              <Link to="/profile-dashboard" style={S.primaryBtnLink}>View My Bookings</Link>
            </div>
          )}

          {status === STATUS_CANCELLED && (
            <div style={S.statusBox}>
              <div style={S.spinnerIcon}>🚫</div>
              <h2 style={S.statusTitle}>Booking cancelled</h2>
              <p style={S.statusText}>
                {order.cancellation_charge
                  ? `A cancellation charge of ${fmt(order.cancellation_charge)} applies.`
                  : "No cancellation charge applies."}
              </p>
              <Link to="/" style={S.primaryBtnLink}>Book Another Service</Link>
            </div>
          )}

          {cancellable && (
            <div style={S.cancelRow}>
              <button style={S.cancelLink} onClick={() => setShowCancelConfirm(true)}>
                Cancel Booking
              </button>
            </div>
          )}

          {error && <div style={S.errorBox}>{error}</div>}
        </div>
      </div>

      {showCancelConfirm && (
        <div style={S.modalOverlay} onClick={() => !cancelling && setShowCancelConfirm(false)}>
          <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={S.modalTitle}>Cancel this booking?</h3>
            <p style={S.modalText}>{previewTier.label} — a <strong>{previewTier.percent}%</strong> cancellation charge applies.</p>
            <div style={S.modalChargeBox}>
              <span>Estimated charge</span>
              <strong>{fmt(previewCharge)}</strong>
            </div>
            <div style={S.modalActions}>
              <button style={S.modalKeepBtn} disabled={cancelling} onClick={() => setShowCancelConfirm(false)}>
                Keep Booking
              </button>
              <button style={S.modalConfirmBtn} disabled={cancelling} onClick={handleCancel}>
                {cancelling ? "Cancelling…" : "Yes, Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  page:      { background:"#f8fafc", minHeight:"100vh", padding:"32px 18px", fontFamily:"Inter,ui-sans-serif,system-ui,sans-serif", color:"#0f172a" },
  container: { maxWidth:"640px", margin:"0 auto" },
  centerNote:{ textAlign:"center", padding:"60px 20px", color:"#64748b", fontSize:"15px" },
  homeLink:  { display:"block", textAlign:"center", color:"#4f46e5", fontWeight:700, textDecoration:"none" },

  card:      { background:"#fff", border:"1px solid #e2e8f0", borderRadius:"22px", padding:"28px", boxShadow:"0 10px 30px rgba(15,23,42,0.05)" },
  headRow:   { display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"14px", marginBottom:"22px" },
  eyebrow:   { margin:0, fontSize:"12px", fontWeight:800, color:"#4f46e5", textTransform:"uppercase", letterSpacing:"0.05em" },
  title:     { margin:"4px 0 4px", fontSize:"22px", fontWeight:900 },
  orderIdText:{ margin:0, fontSize:"12px", color:"#94a3b8" },
  amountBadge:{ background:"#eef2ff", color:"#4338ca", fontWeight:800, fontSize:"16px", padding:"8px 14px", borderRadius:"12px", whiteSpace:"nowrap" },

  stepRow:      { display:"flex", alignItems:"center", justifyContent:"center", gap:"4px", marginBottom:"26px", flexWrap:"wrap" },
  stepItem:     { display:"flex", flexDirection:"column", alignItems:"center", gap:"4px", flexShrink:0 },
  stepDot:      { width:"26px", height:"26px", borderRadius:"50%", background:"#e2e8f0", color:"#64748b", fontSize:"11px", fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center" },
  stepDotActive:{ background:"#4f46e5", color:"#fff" },
  stepLabel:    { fontSize:"10px", fontWeight:700, color:"#94a3b8", whiteSpace:"nowrap" },
  stepLabelActive:{ color:"#0f172a" },
  stepConnector:{ width:"20px", height:"2px", background:"#e2e8f0", marginBottom:"14px" },
  stepConnectorActive:{ background:"#4f46e5" },

  statusBox:  { textAlign:"center", padding:"18px 10px" },
  spinnerIcon:{ fontSize:"44px", marginBottom:"12px" },
  statusTitle:{ margin:"0 0 10px", fontSize:"18px", fontWeight:800 },
  statusText: { margin:"0 0 14px", fontSize:"14px", color:"#64748b", lineHeight:1.6 },

  recapBox:   { textAlign:"left", background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:"14px", padding:"14px 16px", marginBottom:"14px" },
  recapLabel: { margin:"0 0 6px", fontSize:"11px", fontWeight:800, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.05em" },
  recapLine:  { margin:"0 0 4px", fontSize:"14px", color:"#0f172a" },

  otpBox:   { background:"#eef2ff", border:"1px solid #c7d2fe", borderRadius:"14px", padding:"16px", marginBottom:"16px" },
  otpLabel: { margin:"0 0 8px", fontSize:"13px", color:"#4338ca", fontWeight:700 },
  otpCode:  { fontSize:"32px", fontWeight:900, letterSpacing:"8px", color:"#1e1b4b" },

  primaryBtn:    { padding:"14px 26px", borderRadius:"12px", border:"none", background:"linear-gradient(90deg,#4f46e5,#4338ca)", color:"#fff", fontSize:"15px", fontWeight:800, cursor:"pointer" },
  primaryBtnLink:{ display:"inline-block", padding:"14px 26px", borderRadius:"12px", background:"linear-gradient(90deg,#4f46e5,#4338ca)", color:"#fff", fontSize:"15px", fontWeight:800, textDecoration:"none" },
  errorBox:   { marginTop:"16px", background:"#fef2f2", border:"1px solid #fecaca", color:"#dc2626", fontSize:"13px", padding:"12px 14px", borderRadius:"10px" },

  cancelRow: { textAlign:"center", marginTop:"22px", paddingTop:"18px", borderTop:"1px solid #f1f5f9" },
  cancelLink:{ background:"none", border:"none", color:"#dc2626", fontSize:"13px", fontWeight:700, cursor:"pointer", textDecoration:"underline" },

  modalOverlay:  { position:"fixed", inset:0, background:"rgba(15,23,42,0.55)", display:"flex", alignItems:"center", justifyContent:"center", padding:"20px", zIndex:9999 },
  modalCard:     { width:"100%", maxWidth:"400px", background:"#fff", borderRadius:"18px", padding:"24px", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" },
  modalTitle:    { margin:"0 0 10px", fontSize:"18px", fontWeight:900, color:"#0f172a" },
  modalText:     { margin:"0 0 16px", fontSize:"14px", color:"#475569", lineHeight:1.6 },
  modalChargeBox:{ display:"flex", justifyContent:"space-between", alignItems:"center", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:"12px", padding:"12px 16px", marginBottom:"20px", fontSize:"14px", color:"#991b1b" },
  modalActions:  { display:"flex", gap:"10px" },
  modalKeepBtn:  { flex:1, padding:"12px", borderRadius:"10px", border:"1px solid #d5dbe3", background:"#fff", color:"#374151", fontWeight:700, fontSize:"14px", cursor:"pointer" },
  modalConfirmBtn:{ flex:1, padding:"12px", borderRadius:"10px", border:"none", background:"#dc2626", color:"#fff", fontWeight:700, fontSize:"14px", cursor:"pointer" },
};

export default InstantOrderTracking;
